import { NavLink, useNavigate } from "react-router-dom";
import { TitleBar } from "../../components/TitleBar";
import useAuth from "../../hook/useAuth";
import { useEffect, useState } from "react";
import axios from "axios";
import baseURL from "../../config/api";
import { CustomTabs } from "../../components/CustomTabs";
import { UserProgress } from "../../components/UserProgress";
import { getPublishCourses } from "../../services/Course";

const tabs = ["Enrolled Courses", "Enrolled Classes", "Enrolled Parent Courses"];

export const LearnerDashboard = () => {
    const { auth }: any = useAuth();
    const navigate = useNavigate();
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [courses, setCourses] = useState<any[]>([]);
    const [lessons, setLessons] = useState<{ [key: number]: any[] }>({});
    const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([]);
    const [availableCourses, setAvailableCourses] = useState<any[]>([]);
    const [showEnrollModal, setShowEnrollModal] = useState(false);
    const [loadingEnroll, setLoadingEnroll] = useState(false);
    const [classes, setClasses] = useState<any[]>([]);
    const [classCourses, setClassCourses] = useState<any[]>([]);
    const [classLessons, setClassLessons] = useState<{ [key: number]: any[] }>({});
    const [classCourseIsLoaded, setClassCourseIsLoaded] = useState<boolean>(false);
    const [parentCourses, setParentCourses] = useState<any[]>([]);
    const [parentCourseLessons, setParentCourseLessons] = useState<{ [key: number]: any[] }>({});
    const [parentCourseIsLoaded, setParentCourseIsLoaded] = useState<boolean>(false);

    const handleTabChange = (index: number) => setActiveIndex(index);

    useEffect(() => {
        if (!auth?.id) return;
        if (activeIndex === 0) fetchEnrolledCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.id, activeIndex]);

    const fetchEnrolledCourses = async () => {
        try {
            const res = await axios.get(
                `${baseURL}courses/GetEnrolledCoursesByUserId/${auth?.id}`
            );
            setCourses(res?.data || []);
        } catch (err) {
            console.error("Error fetching courses:", err);
        }
    };

    useEffect(() => {
        courses.forEach((course) => {
            if (!lessons[course.id]) {
                axios
                    .get(`${baseURL}lessons/GetLessonsByCourseId/${course.id}`)
                    .then((res) => {
                        setLessons((prev) => ({
                            ...prev,
                            [course.id]: res?.data || [],
                        }));
                    })
                    .catch((err) => console.error("Error fetching lessons:", err));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courses]);

    const fetchAvailableCourses = async () => {
        try {
            getPublishCourses(true)
                .then((data) => {
                    setAvailableCourses(data);
                })
                .catch((error) => {
                    setAvailableCourses([]);
                    console.error(error);
                })

        } catch (err) {
            console.error("Error fetching available courses:", err);
        }
    };

    const handleOpenEnrollModal = async () => {
        await fetchAvailableCourses();
        setShowEnrollModal(true);
    };

    const handleEnrollCourse = async () => {
        if (!selectedCourseIds.length || !auth?.id) return;
        setLoadingEnroll(true);

        try {
            const payloads = selectedCourseIds.map((id) => ({
                courseId: id,
                userId: auth?.id,
                enrolledAt: new Date().toISOString(),
            }));

            await Promise.all(
                payloads.map((data) =>
                    axios.post(`${baseURL}enrolledCourses?userId=${auth?.id}`, data)
                )
            );

            setShowEnrollModal(false);
            setSelectedCourseIds([]);
            await fetchEnrolledCourses();
        } catch (err) {
            console.error("Error enrolling courses:", err);
        } finally {
            setLoadingEnroll(false);
        }
    };

    useEffect(() => {
        if (!auth?.id) return;
        if (activeIndex === 1) {
            setClasses([]);
            setClassCourses([]);
            setClassLessons({});
            setClassCourseIsLoaded(false);

            axios
                .get(`${baseURL}classes/GetEnrolledClassesByStudentId/${auth?.id}`)
                .then((res) => {
                    const data = res.data || [];
                    setClasses(data);
                    if (!data.length) setClassCourseIsLoaded(true);
                })
                .catch((err) => {
                    console.error("Error fetching classes:", err);
                    setClassCourseIsLoaded(true);
                });
        }
    }, [auth?.id, activeIndex]);

    useEffect(() => {
        if (!classes?.length) return;
        const fetchCourses = async () => {
            try {
                const requests = classes
                    .filter((item) => !classCourses[item?.courseId])
                    .map((item) =>
                        axios.get(`${baseURL}courses/${item?.courseId}`).then((res) => ({
                            ...res?.data,
                            classTitle: item?.title,
                        }))
                    );

                if (!requests.length) {
                    setClassCourseIsLoaded(true);
                    return;
                }
                const results = await Promise.all(requests);
                setClassCourses((prev) => [...prev, ...results]);
                setClassCourseIsLoaded(true);
            } catch (err) {
                console.error("Error fetching class courses:", err);
                setClassCourseIsLoaded(true);
            }
        };

        fetchCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes]);

    useEffect(() => {
        if (classCourseIsLoaded) {
            classCourses.forEach((course) => {
                if (!classLessons[course.id]) {
                    axios
                        .get(`${baseURL}lessons/GetLessonsByCourseId/${course.id}`)
                        .then((res) => {
                            setClassLessons((prev) => ({
                                ...prev,
                                [course.id]: res.data || [],
                            }));
                        })
                        .catch((err) => console.error("Error fetching lessons:", err));
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classCourseIsLoaded]);


    useEffect(() => {
        if (!auth?.id) return;
        if (activeIndex === 2) fetchEnrolledParentCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth?.id, activeIndex]);

    const fetchEnrolledParentCourses = async () => {
        try {
            setParentCourseIsLoaded(false)
            const res = await axios.get(
                `${baseURL}courses/GetCoursesAssignedByParent/${auth?.id}`
            );
            if (res?.data.length === 0) {
                setParentCourseIsLoaded(true);
                return;
            }
            setParentCourses(res?.data || []);
            setParentCourseIsLoaded(true);
        } catch (err) {
            console.error("Error fetching courses:", err);
        }
    };

    useEffect(() => {
        if (parentCourseIsLoaded) {
            parentCourses.forEach((course) => {
                if (!parentCourseLessons[course.id]) {
                    axios
                        .get(`${baseURL}lessons/GetLessonsByCourseId/${course.id}`)
                        .then((res) => {
                            setParentCourseLessons((prev) => ({
                                ...prev,
                                [course.id]: res?.data || [],
                            }));
                        })
                        .catch((err) => console.error("Error fetching lessons:", err));
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentCourses]);


    return (
        <div className="p-4 min-h-screen">
            <TitleBar title={`Welcome, ${auth?.givenname}`} subTitle="Learner Dashboard" />
            <UserProgress />
            <div className="w-full pt-6 flex flex-wrap gap-16 border-t border-gray-300">
                <CustomTabs tabs={tabs} activeIndex={activeIndex} onChange={handleTabChange} />

                {activeIndex === 0 && (
                    <>
                        {courses?.length === 0 ? (
                            <div className="w-[450px] max-md:w-full">
                                <div
                                    onClick={handleOpenEnrollModal}
                                    className="bg-blue-50 border border-blue-200 py-10 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-blue-100 transition"
                                >
                                    <div className="w-16 h-16 rounded-full bg-gray-300 flex items-center justify-center mb-2">
                                        <span className="material-symbols-outlined text-3xl text-white">add</span>
                                    </div>
                                    <p className="text-gray-600 font-medium text-sm">
                                        Enroll in a Course
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-6 max-md:w-full">
                                {courses?.map?.((course: any, index: number) => (
                                    <div
                                        key={index}
                                        className="max-md:w-full w-[600px] bg-white border border-[#D0D0D0] rounded-xl shadow-sm p-4"
                                    >
                                        <div className="flex justify-between items-start border-b border-[#D0D0D0] pb-2">
                                            <h2 className="font-semibold text-lg">{course?.title}</h2>
                                        </div>

                                        <p className="text-sm text-gray-600 py-2 font-medium">
                                            {course?.description}
                                        </p>

                                        <div className="mt-4 border rounded-lg overflow-hidden">
                                            <div className="flex justify-between items-center px-2 py-3 bg-gray-50 border-b">
                                                <span className="font-semibold text-sm">Course Preview</span>
                                                <NavLink
                                                    to="/course-lesson"
                                                    state={{
                                                        courseId: course.id,
                                                        lessonId: null,
                                                        isLogin: true,
                                                        isLearner: true,
                                                    }}
                                                    className="text-primary text-sm hover:underline"
                                                >
                                                    See all ({lessons?.[course.id]?.length || 0})
                                                </NavLink>
                                            </div>

                                            <div>
                                                {lessons?.[course?.id]
                                                    ?.slice(0, 5)
                                                    ?.map((lesson: any, idx: number) => (
                                                        <div
                                                            key={idx}
                                                            className="px-4 py-2 border-b last:border-none text-primary text-sm cursor-pointer hover:bg-gray-50 hover:underline"
                                                            onClick={() =>
                                                                navigate("/course-lesson", {
                                                                    state: {
                                                                        courseId: course.id,
                                                                        lessonId: lesson.id,
                                                                        isLogin: true,
                                                                        isLearner: true,
                                                                    },
                                                                })
                                                            }
                                                        >
                                                            {idx + 1}: {lesson?.title}
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div className="w-[450px]">
                                    <div
                                        onClick={handleOpenEnrollModal}
                                        className="bg-blue-50 border border-blue-200 py-10 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-blue-100 transition"
                                    >
                                        <div className="w-16 h-16 rounded-full bg-gray-300 flex items-center justify-center mb-2">
                                            <span className="material-symbols-outlined text-3xl text-white">add</span>
                                        </div>
                                        <p className="text-gray-600 font-medium text-sm">
                                            Enroll another course
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {
                    activeIndex === 1 && (
                        <>
                            {(classCourseIsLoaded && classCourses.length > 0) && (
                                classCourses?.map?.((course: any, index: number) => (
                                    <div
                                        key={index}
                                        className="w-[600px] bg-white border border-[#D0D0D0] rounded-xl shadow-sm p-4 relative max-md:w-full"
                                    >
                                        <div className="flex justify-start items-start border-b border-[#D0D0D0] pb-2">
                                            <h2 className="font-semibold text-lg">{course?.classTitle}</h2>

                                        </div>
                                        <p className="text-sm text-gray-600 mt-1">
                                            Course: <span className="font-medium">{course?.title}</span>
                                        </p>
                                        <p className="text-sm text-gray-600 py-2 font-medium">
                                            {course?.description}
                                        </p>

                                        <div className="mt-4 border rounded-lg overflow-hidden">
                                            <div className="flex justify-between items-center px-2 py-3 bg-gray-50 border-b">
                                                <span className="font-semibold text-sm">Course Preview</span>
                                                <NavLink
                                                    to="/course-lesson"
                                                    state={{
                                                        courseId: course.id,
                                                        lessonId: null,
                                                        isLogin: true,
                                                        isLearner: true
                                                    }}
                                                    className="text-primary text-sm hover:underline"
                                                >
                                                    See all ({classLessons[course.id]?.length || 0})
                                                </NavLink>
                                            </div>
                                            <div>
                                                {classLessons[course.id]
                                                    ?.slice(0, 5)
                                                    ?.map((lesson: any, idx: number) => (
                                                        <div
                                                            key={idx}
                                                            className="px-4 py-2 border-b last:border-none text-primary text-sm cursor-pointer hover:bg-gray-50 hover:underline"
                                                            onClick={() =>
                                                                navigate("/course-lesson", {
                                                                    state: {
                                                                        courseId: course.id,
                                                                        lessonId: lesson.id,
                                                                        isLogin: true,
                                                                        isLearner: true
                                                                    },
                                                                })
                                                            }
                                                        >
                                                            {idx + 1}: {lesson?.title}
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>
                                    </div>
                                )))}

                            {
                                !classCourseIsLoaded && (
                                    <div className="flex items-center justify-center w-full p-2">
                                        <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-300 border-t-primary" />
                                    </div>
                                )
                            }

                            {
                                (classCourseIsLoaded && classCourses.length === 0) && (
                                    <div className="flex items-center justify-center w-full p-2">
                                        <span className="text-lg text-gray-400">No class found!</span>
                                    </div>
                                )
                            }
                        </>

                    )
                }


                {
                    activeIndex === 2 && (
                        <>
                            {
                                (parentCourseIsLoaded && parentCourses.length > 0) && (
                                    <div className="flex flex-wrap gap-6">
                                        {parentCourses?.map?.((course: any, index: number) => (
                                            <div
                                                key={index}
                                                className="w-[600px] bg-white border border-[#D0D0D0] rounded-xl shadow-sm p-4 max-md:w-full"
                                            >
                                                <div className="flex justify-between items-start border-b border-[#D0D0D0] pb-2">
                                                    <h2 className="font-semibold text-lg">{course?.title}</h2>
                                                </div>

                                                <p className="text-sm text-gray-600 py-2 font-medium">
                                                    {course?.description}
                                                </p>

                                                <div className="mt-4 border rounded-lg overflow-hidden">
                                                    <div className="flex justify-between items-center px-2 py-3 bg-gray-50 border-b">
                                                        <span className="font-semibold text-sm">Course Preview</span>
                                                        <NavLink
                                                            to="/course-lesson"
                                                            state={{
                                                                courseId: course.id,
                                                                lessonId: null,
                                                                isLogin: true,
                                                                isLearner: true,
                                                            }}
                                                            className="text-primary text-sm hover:underline"
                                                        >
                                                            See all ({parentCourseLessons?.[course.id]?.length || 0})
                                                        </NavLink>
                                                    </div>

                                                    <div>
                                                        {parentCourseLessons?.[course?.id]
                                                            ?.slice(0, 5)
                                                            ?.map((lesson: any, idx: number) => (
                                                                <div
                                                                    key={idx}
                                                                    className="px-4 py-2 border-b last:border-none text-primary text-sm cursor-pointer hover:bg-gray-50 hover:underline"
                                                                    onClick={() =>
                                                                        navigate("/course-lesson", {
                                                                            state: {
                                                                                courseId: course.id,
                                                                                lessonId: lesson.id,
                                                                                isLogin: true,
                                                                                isLearner: true,
                                                                            },
                                                                        })
                                                                    }
                                                                >
                                                                    {idx + 1}: {lesson?.title}
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                    </div>
                                )
                            }


                            {!parentCourseIsLoaded && (
                                <div className="flex items-center justify-center w-full p-2">
                                    <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-300 border-t-primary" />
                                </div>
                            )}

                            {parentCourseIsLoaded && parentCourses.length === 0 && (
                                <div className="flex items-center justify-center w-full p-2">
                                    <span className="text-lg text-gray-400">No courses found!</span>
                                </div>
                            )}
                        </>


                    )
                }
            </div>

            {showEnrollModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 w-[700px] shadow-lg">
                        <h2 className="text-lg font-semibold mb-4">Select Courses to Enroll</h2>

                        <div className="grid grid-cols-3 gap-3 max-h-[400px] overflow-y-auto">
                            {availableCourses?.map?.((course) => {
                                const isAlreadyEnrolled = courses.some(
                                    (c) => c.id === course.id
                                );
                                const isSelected = selectedCourseIds.includes(course.id);

                                const handleToggle = () => {
                                    if (isAlreadyEnrolled) return;
                                    setSelectedCourseIds((prev) =>
                                        isSelected
                                            ? prev.filter((id) => id !== course.id)
                                            : [...prev, course.id]
                                    );
                                };

                                return (
                                    <div
                                        key={course.id}
                                        className={`border rounded-lg p-3 cursor-pointer flex flex-col gap-1 transition ${isAlreadyEnrolled
                                            ? "bg-gray-100 border-gray-300 opacity-70 cursor-not-allowed"
                                            : isSelected
                                                ? "bg-blue-50 border-blue-500"
                                                : "border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                                            }`}
                                        onClick={handleToggle}
                                    >
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={isSelected || isAlreadyEnrolled}
                                                disabled={isAlreadyEnrolled}
                                                onChange={handleToggle}
                                                className="w-4 h-4"
                                            />
                                            <h3 className="font-medium text-sm">{course?.title || "Untitled"}</h3>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => setShowEnrollModal(false)}
                                className="px-4 py-2 border rounded-lg text-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                disabled={!selectedCourseIds.length || loadingEnroll}
                                onClick={handleEnrollCourse}
                                className={`px-4 py-2 rounded-lg text-white ${loadingEnroll
                                    ? "bg-gray-400"
                                    : "bg-primary hover:bg-primary/90"
                                    }`}
                            >
                                {loadingEnroll ? "Enrolling..." : "Continue"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};